/* eslint-disable max-len */
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Icon, RotatingSpinner } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func } from 'prop-types'

import { useLocation } from 'Pods/Shared/ContextBasedRouter'
import { LIBRARY_TAB, STUDIO_PATH } from 'Pods/ThreeDStudio/constants'
import useGenerationConcurrency from 'Pods/ThreeDStudio/Hooks/useGenerationConcurrency'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { VIEWER_SIDEBAR_TAB } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import EmptyState from './Components/EmptyState/EmptyState'
import ImageCard from './Components/ImageCard/ImageCard'
import LibraryTabs from './Components/LibraryTabs/LibraryTabs'
import LoadingModelCard from './Components/LoadingModelCard/LoadingModelCard'
import ModelCard from './Components/ModelCard/ModelCard'
import NewImageCard from './Components/NewImageCard/NewImageCard'
import NewModelCard from './Components/NewModelCard/NewModelCard'
import NotFoundState from './Components/NotFoundState/NotFoundState'
import SearchInput from './Components/SearchInput/SearchInput'
import UseModelCard from './Components/UseModelCard/UseModelCard'
import { mockedModel } from './Constants/mock'
import useGenerateNewImage from './Hooks/useGenerateNewImage'
import useGenerateNewModel from './Hooks/useGenerateNewModel'
import useGetLibraryItems from './Hooks/useGetLibraryItems'

const size = { height: '32px', width: '32px' }

// The tab a generator's routes select: the Image Generator (and an image opened on it) shows
// Images, the 3D generator (and a model opened on it) shows 3D Models. Every other route,
// such as the Community galleries, leaves the tab as it is.
const libraryTabForPath = (pathname) => {
  if (pathname === STUDIO_PATH.IMAGE_GENERATOR || pathname.startsWith(`${STUDIO_PATH.IMAGE_GENERATOR}/`)) {
    return LIBRARY_TAB.IMAGES
  }
  if (pathname === STUDIO_PATH.GENERATE || pathname.startsWith(`${STUDIO_PATH.COMMUNITY_THREE_D_MODELS}/`)) {
    return LIBRARY_TAB.MODELS
  }

  return null
}

function LibraryContent({ onNavigate }) {
  const t = useTranslation()
  const pageContext = useContext(ThreeDStudioPageContext)
  const isLoggedIn = pageContext?.userLoggedIn
  const pageConfigLoaded = pageContext?.pageConfigLoaded
  const extractingCoreStyles = pageContext?.extractingCoreStyles
  const twoDImagesIdeationFlowEnabled = pageContext?.twoDImagesIdeationFlowEnabled
  const activeRuns = useThreeDStudioStore(state => state.generation.activeRuns)
  const { isPaidTier, isMultipleEnabled } = useGenerationConcurrency()
  const scrollContainerRef = useRef(null)
  const sentinelRef = useRef(null)
  const [keywords, setKeywords] = useState('')
  const [searchResetKey, setSearchResetKey] = useState(0)
  const location = useLocation()
  const routeTab = twoDImagesIdeationFlowEnabled ? libraryTabForPath(location?.pathname ?? '') : null
  const [activeTab, setActiveTab] = useState(routeTab ?? LIBRARY_TAB.MODELS)
  // The flag rides along because it can turn on after the first render (pageConfig replaces
  // the actorless SSR value), and a visitor who opened /ai-image directly makes no navigation.
  const navigationKey = `${location?.key}:${twoDImagesIdeationFlowEnabled}`
  const [tabNavigationKey, setTabNavigationKey] = useState(navigationKey)
  const libraryTabRequest = useThreeDStudioStore(state => state.ui.libraryTabRequest)
  const [handledTabRequest, setHandledTabRequest] = useState(libraryTabRequest)

  const isImagesTab = activeTab === LIBRARY_TAB.IMAGES
  // A Render tab run produces a 2D image that opens in the Render preview and lands in the
  // Images tab only once it finishes, so it is neither a variation of its source model
  // nor a model being generated: it gets no overlay and no loading card here.
  const modelRuns = useMemo(
    () => activeRuns.filter(run => run.triggeredBy !== VIEWER_SIDEBAR_TAB.RENDER),
    [activeRuns]
  )
  // Only 3D generations show here, so an in-flight one must not make the Images tab look
  // busy or keep its placeholder from showing.
  const isGenerating = !isImagesTab && modelRuns.length > 0

  // Each badge reads its own unfiltered query, so both counts load with the page rather than
  // when their tab opens, and a search does not change them. They share the cache entry the
  // tab reads, so the open tab costs no extra request. The images one skips polling: while
  // its tab is open, the tab's own query polls.
  const modelsCount = useGetLibraryItems(isLoggedIn, '', LIBRARY_TAB.MODELS).meta?.totalCount
  const imagesCount = useGetLibraryItems(
    isLoggedIn === true && twoDImagesIdeationFlowEnabled === true,
    '',
    LIBRARY_TAB.IMAGES,
    false
  ).meta?.totalCount

  const {
    items,
    hasNextPage,
    handleLoadMore,
    isFetchingNextPage,
    isLoading,
    isError,
    meta,
  } = useGetLibraryItems(isLoggedIn, keywords, activeTab)

  // A variation (an enhancement, or a re-generation that joined the opened model's
  // session) renders as a "Processing..." overlay on its session's card; every other
  // in-flight run gets its own "Generating..." card. A variation whose session is not in
  // the loaded page - a deep-linked model further down the list, or an AI Assistant
  // session that holds no model yet and so is absent from the library - has no card to
  // overlay, so it falls back to a "Generating..." card rather than to no spinner at all.
  const loadedSessionIds = useMemo(
    () => new Set(items.map(({ id }) => String(id))),
    [items]
  )
  const isOverlaidVariation = useCallback(
    run => run.enhancingSessionId != null && loadedSessionIds.has(String(run.enhancingSessionId)),
    [loadedSessionIds]
  )
  const loadingRuns = useMemo(
    () => modelRuns.filter(run => !isOverlaidVariation(run)),
    [modelRuns, isOverlaidVariation]
  )
  const processingSessionIds = useMemo(
    () => new Set(
      modelRuns
        .filter(isOverlaidVariation)
        .map(run => String(run.enhancingSessionId))
    ),
    [modelRuns, isOverlaidVariation]
  )
  // Paid tiers keep the "Generate New 3D Model" card while generating - at the cap too,
  // where it still opens the starting page and only the Generate button is blocked. Free
  // tiers - and everyone while the :enable_multiple_generations flag is off - keep the
  // legacy behavior of swapping it for the single "Generating..." card.
  const showNewModelCard = (isMultipleEnabled && isPaidTier) || loadingRuns.length === 0

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    const sentinel = sentinelRef.current
    if (!scrollContainer || !sentinel || !hasNextPage || isFetchingNextPage) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          handleLoadMore()
        }
      },
      { root: scrollContainer, rootMargin: '100px', threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, handleLoadMore])

  const remainingCount = Math.max(0, (meta?.totalCount ?? 0) - items.length)
  const loadingMoreSkeletonCount = Math.min(
    remainingCount,
    Number(meta?.perPage) || 10
  )
  const shouldShowSkeletonItems = !extractingCoreStyles
    && items.length > 0
    && hasNextPage
    && isFetchingNextPage
    && loadingMoreSkeletonCount > 0
  const authPending = !extractingCoreStyles && !pageConfigLoaded
  const shouldShowLoading = !extractingCoreStyles && !isError && (authPending || (isLoggedIn && isLoading))
  const shouldShowError = isError && !isLoading
  const shouldShowContent = extractingCoreStyles || (!authPending && !isLoading && (items.length > 0 || isGenerating))
  const shouldShowPlaceholder = !extractingCoreStyles
    && !authPending
    && !isLoading
    && !isError
    && !isGenerating
    && items.length === 0
  const hasSearched = keywords.length > 0
  const shouldShowEmptyState = shouldShowPlaceholder && !hasSearched
  const shouldShowNotFound = shouldShowPlaceholder && hasSearched
  const shouldShowSearch = extractingCoreStyles || (meta?.totalCount ?? 0) >= 10 || keywords.length > 0
  // An empty library still leads with the two action cards, so the grid renders with just
  // those and the placeholder copy sits below it. A search that found nothing keeps them too:
  // they are the only way back to a new generation now that the not-found state has no button
  // of its own. The Images tab never shows them: they both start a 3D generation.
  const shouldShowActionCards = !isImagesTab && (shouldShowContent || shouldShowPlaceholder)
  // The Images tab leads its grid with its own card the same way, empty or not.
  const shouldShowNewImageCard = isImagesTab && (shouldShowContent || shouldShowPlaceholder)
  const shouldShowGrid = shouldShowContent || shouldShowActionCards || shouldShowNewImageCard

  const handleClearSearch = useCallback(() => {
    setKeywords('')
    // Remount SearchInput to reset its internal input value (and pending debounce).
    setSearchResetKey(key => key + 1)
  }, [])

  const handleSelectTab = useCallback((nextTab) => {
    setActiveTab((previousTab) => {
      if (previousTab !== nextTab) {
        handleClearSearch()
      }
      return nextTab
    })
  }, [handleClearSearch])

  // Each navigation into a generator moves the tab to that generator's, even one that stays on
  // the same path (its nav item clicked again), while a tab picked by hand holds until the
  // next navigation. Applied while rendering, keyed on location.key, so the grid never
  // paints a frame of the previous tab.
  if (navigationKey !== tabNavigationKey) {
    setTabNavigationKey(navigationKey)
    if (routeTab && routeTab !== activeTab) {
      setActiveTab(routeTab)
      handleClearSearch()
    }
  }

  // A tab asked for from elsewhere (a finished render shows its image under Images), applied
  // the same way. The Images tab only exists behind the ideation flag.
  if (libraryTabRequest !== handledTabRequest) {
    setHandledTabRequest(libraryTabRequest)
    const requestedTab = libraryTabRequest?.tab
    const isRequestedTabAvailable = requestedTab !== LIBRARY_TAB.IMAGES || twoDImagesIdeationFlowEnabled
    if (requestedTab && isRequestedTabAvailable && requestedTab !== activeTab) {
      setActiveTab(requestedTab)
      handleClearSearch()
    }
  }

  const handleGenerateImage = useGenerateNewImage(onNavigate)
  const handleGenerateModel = useGenerateNewModel(onNavigate)

  // Each tab's empty copy carries its own generator's action as a button under it.
  const emptyStateAction = isImagesTab ? handleGenerateImage : handleGenerateModel

  const tabs = useMemo(() => [
    { id: LIBRARY_TAB.MODELS, label: t('sidebar.library.tabs.models'), count: modelsCount },
    { id: LIBRARY_TAB.IMAGES, label: t('sidebar.library.tabs.images'), count: imagesCount },
  ], [t, modelsCount, imagesCount])

  return (
    <div className="library-content">
      <h2 className="library-content__title cgt-caption">{t('sidebar.library.title')}</h2>
      {twoDImagesIdeationFlowEnabled && (
        <LibraryTabs tabs={tabs} activeTab={activeTab} onSelect={handleSelectTab} />
      )}
      {shouldShowSearch && <SearchInput key={`${activeTab}-${searchResetKey}`} onSearch={setKeywords} />}
      {/* Everything the open tab shows - grid, placeholder copy, loading and error - sits
          inside the panel, so the copy is announced as that tab's content instead of as a
          sibling of the grid, and the tab's aria-controls always resolves. */}
      <div
        className="library-content__panel"
        role={twoDImagesIdeationFlowEnabled ? 'tabpanel' : undefined}
        id={twoDImagesIdeationFlowEnabled ? `library-tabpanel-${activeTab}` : undefined}
        aria-labelledby={twoDImagesIdeationFlowEnabled ? `library-tab-${activeTab}` : undefined}
      >
        {shouldShowLoading && (
          <div className="library-content__loading">
            <Icon svgComponent={RotatingSpinner} size={size} />
          </div>
        )}
        {shouldShowError && (
          <div className="library-content__error">{t('sidebar.library.error')}</div>
        )}
        {shouldShowGrid && (
          <div ref={scrollContainerRef} className="model-card-list">
            {shouldShowActionCards && showNewModelCard && <NewModelCard onNavigate={onNavigate} />}
            {shouldShowNewImageCard && <NewImageCard onGenerate={handleGenerateImage} />}
            {/* Not gated on showNewModelCard: bringing your own model is not a generation,
                so it stays available while one is running. */}
            {shouldShowActionCards && <UseModelCard />}
            {!isImagesTab && loadingRuns.map(run => (
              <LoadingModelCard
                key={run.clientId ?? `run-${run.runId}`}
                run={run}
                isActiveOnAiPage={!showNewModelCard}
                onNavigate={onNavigate}
                content={t('sidebar.status.generating')}
              />
            ))}
            {!extractingCoreStyles && isImagesTab && items.map(({ id, ...rest }) => (
              <ImageCard
                key={`image-card-${id}`}
                runId={id}
                isProcessing={rest.attributes?.status !== 'succeeded'}
                onNavigate={onNavigate}
                {...rest}
              />
            ))}
            {!extractingCoreStyles && !isImagesTab && items.map(({ id, ...rest }) => (
              <ModelCard
                key={`model-card-${id}`}
                sessionId={id}
                isProcessing={processingSessionIds.has(String(id))}
                onNavigate={onNavigate}
                {...rest}
              />
            ))}
            {extractingCoreStyles && (
              <>
                <ModelCard sessionId={mockedModel.id} attributes={mockedModel.attributes} onNavigate={onNavigate} />
                <ModelCard
                  sessionId={mockedModel.id}
                  attributes={mockedModel.attributes}
                  isProcessing
                  onNavigate={onNavigate}
                />
                <LoadingModelCard onNavigate={onNavigate} content={t('sidebar.status.generating')} />
                <div className="library-content__loading-more">
                  <LoadingModelCard />
                </div>
              </>
            )}
            {shouldShowSkeletonItems && (
              <div className="library-content__loading-more">
                {Array.from({ length: loadingMoreSkeletonCount }, (_, i) => (
                  <LoadingModelCard key={`loading-more-${i}`} isImagesTab={isImagesTab} />
                ))}
              </div>
            )}
            {items.length > 0 && hasNextPage && (
              <div
                ref={sentinelRef}
                className="library-content__sentinel"
                aria-hidden="true"
              />
            )}
          </div>
        )}
        {shouldShowNotFound && (
          <NotFoundState tab={activeTab} onGenerate={isImagesTab ? handleGenerateImage : null} />
        )}
        {shouldShowEmptyState && (
          <EmptyState tab={activeTab} onGenerate={emptyStateAction} />
        )}
      </div>
    </div>
  )
}

LibraryContent.propTypes = {
  onNavigate: func.isRequired,
}

export default memo(LibraryContent)
